import { Box, useMediaQuery, useTheme } from '@mui/material';
import Head from 'next/head';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { Footer } from '@shared-components-web';
import GlobalErrorDialog from '@src/components/GlobalErrorDialog';
import Header from '@src/components/Header';
import MobileMenu from '@src/components/MobileMenu';
import { UserPortalSidebarList } from '@src/components/UserPortalSidebar/UserPortalSidebar';
import { AdFooter, AnalyticsContext, App, Maybe } from '@src/lib/__generated/contentful/sdk';
import { getElementID, getQualtricsInterceptSnippet, QUALTRICS_INTERCEPT_ID } from '@src/lib/qualtrics-helpers';
interface LayoutPropsInterface {
	children: ReactNode;
	appData: Maybe<App>;
	footerData: Maybe<AdFooter>;
	withSimpleLayout?: boolean;
	logoHref: string;
}

const renderFooter = (footerData: Maybe<AdFooter>, withSimpleLayout?: boolean) => {
	if (!footerData) return null;
	return <Footer footerData={footerData as any} withScheduling={withSimpleLayout} />;
};

export const HEADER_HEIGHT = '4.8125rem'; // 77px

const Layout = ({ appData, footerData, withSimpleLayout, children, logoHref }: LayoutPropsInterface) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const [isClient, setIsClient] = useState(false);
	const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
	const headerRef = useRef<HTMLDivElement | null>(null);
	const pathname = usePathname();
	const resolvedPathname = pathname ?? '';

	useEffect(() => {
		setIsClient(true);
		const scopedBaseline = document.getElementById('mui-scoped-baseline');
		if (scopedBaseline) {
			scopedBaseline.style.height = '100vh'; // fallback for old browsers
			scopedBaseline.style.height = '100dvh'; // visible viewport (ignored if unsupported)
		}
	}, []);

	useEffect(() => {
		const scopedBaseline = document.getElementById('mui-scoped-baseline');
		// Apply user-portal/appointments page to allow sticky header for year categorization
		if (scopedBaseline?.style && resolvedPathname.includes('/user-portal/appointments')) {
			scopedBaseline.style.overflowX = isSmallScreen ? 'unset' : 'hidden';
		}
		if (!isSmallScreen) {
			setIsNavMenuOpen(false);
		}
	}, [isSmallScreen, resolvedPathname]);

	if (!isClient) {
		return null;
	}

	const pageBrandTitle = 'Lovet Pet Health Care';
	const pageTitle = resolvedPathname.includes('/user-portal') ? 'Client Portal' : 'Book An Appointment';
	const qualtricsSiteInterceptId = QUALTRICS_INTERCEPT_ID;
	const qualtricsSnippet = getQualtricsInterceptSnippet();
	const qualtricsEnabled = !!appData?.config?.qualtricsEnabled;

	const getIsNavMenuOpen = isSmallScreen && isNavMenuOpen;

	return (
		<Box
			sx={{
				minHeight: '100dvh',
				display: 'flex',
				flexDirection: 'column',
				bgcolor:
					resolvedPathname.startsWith('/user-portal/previous-appointments') && isSmallScreen
						? 'backgrounds.white'
						: 'backgrounds.neutral',
				pt: HEADER_HEIGHT, // account for fixed header height 77px
			}}
		>
			<Head>
				<title data-test-id="page-title">
					{pageTitle} | {pageBrandTitle}
				</title>
			</Head>
			{qualtricsEnabled && (
				<>
					<Script
						id={getElementID(qualtricsSiteInterceptId)}
						strategy="afterInteractive"
						dangerouslySetInnerHTML={{ __html: qualtricsSnippet }}
					/>
					<div
						id={`qt-dn-${qualtricsSiteInterceptId}`}
						style={{ display: 'none' }}
					>{`<!--DO NOT REMOVE-CONTENTS PLACED HERE-->`}</div>
				</>
			)}
			{appData && (
				<Header
					appData={appData}
					logoHref={logoHref}
					isSmallScreen={isSmallScreen}
					isNavMenuOpen={getIsNavMenuOpen}
					setIsNavMenuOpen={setIsNavMenuOpen}
					ref={headerRef}
					logoLinkAnalyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
				/>
			)}
			<Box sx={{ flexGrow: 1 }}>{children}</Box>
			{renderFooter(footerData, withSimpleLayout)}
			<GlobalErrorDialog />

			<MobileMenu
				open={isNavMenuOpen}
				onClose={() => setIsNavMenuOpen(false)}
				anchorEl={headerRef.current || undefined}
			>
				<UserPortalSidebarList allowScheduleButton onClose={() => setIsNavMenuOpen(false)} />
			</MobileMenu>
		</Box>
	);
};

export default Layout;
